import { t } from "@lingui/macro";
import { AnalyzeResumeResult } from "@reactive-resume/schema";
import { useTheme } from "@reactive-resume/hooks";
import { cn } from "@reactive-resume/utils";
import { CheckCircle2, XCircle, Info, AlertTriangle, Plus, ArrowRight, ChevronDown, ChevronUp } from "lucide-react";
import { useState } from "react";

export type SkillType = "hard" | "soft";
export type AnalysisType = "skill" | "keyword";

type SkillSimilarityProps = {
  analysesData: AnalyzeResumeResult;
  analysisType: AnalysisType;
  skillType?: SkillType;
  className?: string;
};

export const SkillSimilarity = ({
  analysesData,
  analysisType,
  skillType = "hard",
  className,
}: SkillSimilarityProps) => {
  const { isDarkMode } = useTheme();
  const [showAllMatching, setShowAllMatching] = useState(false);
  const [showAllMissing, setShowAllMissing] = useState(false);

  // Initial number of items to show
  const INITIAL_ITEMS_TO_SHOW = 8;

  // Get the appropriate metric data based on analysis type
  let metricKey = "";
  let metric;

  if (analysisType === "skill") {
    metricKey = skillType === "hard" ? "hss" : "ssa";
    metric = analysesData.metrics[metricKey as keyof typeof analysesData.metrics];
  } else {
    metricKey = "kc";
    metric = analysesData.metrics[metricKey as keyof typeof analysesData.metrics];
  }

  // Return null if data doesn't exist
  if (!metric || !metric.object) {
    return null;
  }

  // Define colors - unified with metric-score-card.tsx
  const colors = {
    green: isDarkMode ? "#66bb6a" : "#81c784",
    red: isDarkMode ? "#ef5350" : "#e57373",
    amber: "#ffb74d",
    blue: "#4dabf5",
    greenBg: isDarkMode ? "rgba(102, 187, 106, 0.15)" : "rgba(129, 199, 132, 0.1)",
    redBg: isDarkMode ? "rgba(239, 83, 80, 0.15)" : "rgba(229, 115, 115, 0.1)",
    amberBg: isDarkMode ? "rgba(255, 183, 77, 0.15)" : "rgba(255, 183, 77, 0.1)",
    blueBg: isDarkMode ? "rgba(77, 171, 245, 0.15)" : "rgba(77, 171, 245, 0.1)",
  };

  // Process data based on analysis type
  let jobItems: string[] = [];
  let resumeItems: string[] = [];
  let title = "";
  let description = "";
  let matchingDescription = "";
  let missingDescription = "";
  let actionAdvice = "";

  if (analysisType === "skill" && skillType === "hard") {
    const hssData = metric.object as any;
    // Get high-confidence matches only to reduce noise
    const skillMatches = (hssData.skill_matches || []).filter(
      (match: any) => match.confidence === "high" || match.confidence === "medium",
    );

    jobItems = (hssData.job_hard_skills || [])
      .filter(
        (skill: any) => skill.importance === "must_have" || skill.importance === "highly_preferred",
      )
      .map((skill: any) => skill.skill);

    resumeItems = skillMatches.map((match: any) => match.job_skill);

    title = t`Technical Skills Match`;
    description = t`How well your technical skills align with the job requirements. Focus on the most important skills.`;
    matchingDescription = t`Skills you have that match the job requirements. Highlight these prominently in your resume.`;
    missingDescription = t`Important skills you're missing. Consider learning these or finding equivalent skills to mention.`;
    actionAdvice = t`Focus on the missing skills that are most relevant to your field. Look for online courses or certifications to fill these gaps.`;
  } else if (analysisType === "skill" && skillType === "soft") {
    const ssaData = metric.object as any;
    jobItems = ssaData.job_soft_skills || [];
    resumeItems = ssaData.resume_soft_skills || [];

    title = t`Soft Skills Match`;
    description = t`How well your interpersonal and professional skills align with what the employer is looking for.`;
    matchingDescription = t`Soft skills you demonstrate that the job requires. Make sure these are clearly shown in your experience.`;
    missingDescription = t`Important soft skills to highlight. Add examples of these skills from your work experience.`;
    actionAdvice = t`Think of specific examples from your work that demonstrate these soft skills, then add them to your resume.`;
  } else {
    const kcData = metric.object as any;
    jobItems = kcData.job_keywords || [];
    resumeItems = kcData.resume_keywords || [];

    title = t`Keyword Match`;
    description = t`Important terms from the job description that should appear in your resume to pass initial screenings.`;
    matchingDescription = t`Keywords from the job description that already appear in your resume.`;
    missingDescription = t`Important keywords missing from your resume. Add these naturally where they fit.`;
    actionAdvice = t`Incorporate missing keywords naturally into your experience descriptions, but avoid keyword stuffing.`;
  }

  // Calculate matches
  const matchingItems = jobItems.filter((item) => resumeItems.includes(item));
  const missingItems = jobItems.filter((item) => !resumeItems.includes(item));
  // Get match percentage from analysis results - same calculation as metric-score-card.tsx
  const score = metric.weight > 0 ? metric.weightedScore / metric.weight : 0;
  const matchPercentage = Math.round(score * 100);

  // Determine match status - unified with metric-score-card.tsx
  let matchStatus: "excellent" | "good" | "needs_work" = "needs_work";
  if (matchPercentage >= 80) matchStatus = "excellent";
  else if (matchPercentage >= 60) matchStatus = "good";

  const getStatusInfo = () => {
    switch (matchStatus) {
      case "excellent":
        return {
          text: t`Excellent`,
          description: t`This aspect of your resume is working very well for this job.`,
          action: t`Keep doing what you're doing - this is a strength!`,
          bgColor: colors.greenBg,
          textColor: colors.green,
        };
      case "good":
        return {
          text: t`Good`,
          description: t`This area is decent but could be improved for better results.`,
          action: t`Review the suggestions below to make this even stronger.`,
          bgColor: colors.blueBg,
          textColor: colors.blue,
        };
      default:
        return {
          text: t`Needs Work`,
          description: t`This area needs significant improvement to match the job requirements.`,
          action: t`Focus on the recommendations below to improve your chances.`,
          bgColor: colors.redBg,
          textColor: colors.red,
        };
    }
  };

  const statusInfo = getStatusInfo();

  return (
    <div className={cn("grid gap-y-6", className)}>
      <div className="grid gap-y-5">
        {/* Status Overview */}
        <div
          className="rounded-lg p-4 transition-colors"
          style={{
            backgroundColor: statusInfo.bgColor,
            borderLeft: `4px solid ${statusInfo.textColor}`,
          }}
        >
          <div className="mb-2 flex items-center gap-x-2">
            <Info className="h-5 w-5" style={{ color: statusInfo.textColor }} />
            <span className="text-lg font-semibold" style={{ color: statusInfo.textColor }}>
              {title}: {statusInfo.text} ({matchPercentage}%)
            </span>
          </div>
          <p className="mb-2 text-sm text-slate-700 dark:text-slate-300">{description}</p>
          <div
            className="flex items-center gap-x-2 text-sm font-medium"
            style={{ color: statusInfo.textColor }}
          >
            <ArrowRight className="h-4 w-4" />
            <span>{statusInfo.action}</span>
          </div>
        </div>

        {/* Missing Items - What you need (TOP) */}
        {missingItems.length > 0 && (
          <div>
            <div className="mb-3">
              <h4 className="mb-1 flex items-center gap-x-2 text-base font-semibold">
                <AlertTriangle className="h-5 w-5 text-red-600" />
                {t`You Need These (${missingItems.length})`}
              </h4>
              <p className="text-sm text-slate-600 dark:text-slate-400">{missingDescription}</p>
            </div>
            <div className="flex flex-wrap gap-2">
              {missingItems
                .slice(0, showAllMissing ? missingItems.length : INITIAL_ITEMS_TO_SHOW)
                .map((item) => (
                  <div
                    key={item}
                    className="flex items-center gap-x-2 rounded-lg border px-3 py-2 text-sm font-medium"
                    style={{
                      backgroundColor: colors.redBg,
                      borderColor: colors.red,
                      color: colors.red,
                    }}
                  >
                    <Plus className="h-4 w-4" />
                    <span>{item.replace(/_/g, " ")}</span>
                  </div>
                ))}
              {missingItems.length > INITIAL_ITEMS_TO_SHOW && (
                <button
                  onClick={() => setShowAllMissing(!showAllMissing)}
                  className="mt-3 flex w-full items-center justify-center gap-x-2 rounded-lg border border-slate-200 bg-white p-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
                >
                  {showAllMissing ? (
                    <>
                      <ChevronUp className="h-4 w-4" />
                      {t`Show Less`}
                    </>
                  ) : (
                    <>
                      <ChevronDown className="h-4 w-4" />
                      {t`Show ${missingItems.length - INITIAL_ITEMS_TO_SHOW} More`}
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        )}

        {/* Matching Items - What you have */}
        {matchingItems.length > 0 && (
          <div>
            <div className="mb-3">
              <h4 className="mb-1 flex items-center gap-x-2 text-base font-semibold">
                <CheckCircle2 className="h-5 w-5 text-green-600" />
                {t`You Have These (${matchingItems.length})`}
              </h4>
              <p className="text-sm text-slate-600 dark:text-slate-400">{matchingDescription}</p>
            </div>
            <div className="flex flex-wrap gap-2">
              {matchingItems
                .slice(0, showAllMatching ? matchingItems.length : INITIAL_ITEMS_TO_SHOW)
                .map((item) => (
                  <div
                    key={item}
                    className="flex items-center gap-x-2 rounded-lg border px-3 py-2 text-sm font-medium"
                    style={{
                      backgroundColor: colors.greenBg,
                      borderColor: colors.green,
                      color: colors.green,
                    }}
                  >
                    <CheckCircle2 className="h-4 w-4" />
                    <span>{item.replace(/_/g, " ")}</span>
                  </div>
                ))}
              {matchingItems.length > INITIAL_ITEMS_TO_SHOW && (
                <button
                  onClick={() => setShowAllMatching(!showAllMatching)}
                  className="mt-3 flex w-full items-center justify-center gap-x-2 rounded-lg border border-slate-200 bg-white p-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
                >
                  {showAllMatching ? (
                    <>
                      <ChevronUp className="h-4 w-4" />
                      {t`Show Less`}
                    </>
                  ) : (
                    <>
                      <ChevronDown className="h-4 w-4" />
                      {t`Show ${matchingItems.length - INITIAL_ITEMS_TO_SHOW} More`}
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        )}

        {/* No matches found */}
        {matchingItems.length === 0 && missingItems.length === 0 && (
          <div className="rounded-lg bg-slate-50 p-4 text-center dark:bg-slate-800/50">
            <Info className="mx-auto mb-2 h-8 w-8 text-slate-400" />
            <p className="text-sm text-slate-600 dark:text-slate-400">
              {analysisType === "skill"
                ? t`No skill data available for analysis.`
                : t`No keyword data available for analysis.`}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
